import * as api from 'config/api';
import React from 'react';
import { yyyymmddToDate } from 'components/features/new-report/daily-report/DailyReport';

export type ColumnTableProps = {
  columnRowData: ColumnRowProps[];
  userName: string;
};

const ARTICLE_TYPE = {
  MONTHLY_COLUMN: 1,
  STEP_FORWARD_COLUMN: 2,
  KNOWLEDGE_COLUMN: 3,
} as const;

type ArticleType = typeof ARTICLE_TYPE[keyof typeof ARTICLE_TYPE];

type HintProps = {
  userName: string;
};

type ColumnProps = {
  title: string;
  imgSrc: string;
  link: string;
};

type ColumnRowProps = {
  title: string;
  columnData: ColumnProps[];
};

const getArticleCategoryTitle = (type: ArticleType, month?: number) => {
  switch (type) {
    case ARTICLE_TYPE.MONTHLY_COLUMN:
      return `${month}月に読みたいコラム`;
    case ARTICLE_TYPE.KNOWLEDGE_COLUMN:
      return '投資入門講座';
    case ARTICLE_TYPE.STEP_FORWARD_COLUMN:
      return '資産形成のお悩み解決';
  }
};

const Hint: React.FC<HintProps> = ({ userName }) => {
  return (
    <>
      <div className="p-4 overflow-hidde">
        <div className="py-2 px-4 bg-bluegray-50 rounded-7">
          <div className="mb-1 text-xs text-teal-500 leading-6">
            活用のヒント
          </div>
          <div className="flex">
            <div className="h-12.5 w-12.5 min-w-12.5 mr-2 rounded-full">
              <img
                src="/static/images/advisor/ai_img_2.svg"
                className="rounded-full"
                alt=""
              />
            </div>
            <div className="text-sm text-bluegray-700">
              <div className="text-xs font-bold">
                投資の知識を育てていきましょう
              </div>
              <div className="mt-1 text-sm">
                {userName}
                さんにおすすめのコラムを集めてみました。気になる内容があれば読んでみてくださいね。
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const Column: React.FC<ColumnProps> = ({ title, imgSrc, link }) => {
  return (
    <>
      <li className="asset-info-banner-list-item">
        <a id="monthlyreport_column" href={link}>
          <img src={imgSrc} className="asset-info-banner" alt="" />
          <div className="asset-info-banner-list-item-label">{title}</div>
        </a>
      </li>
    </>
  );
};

const ColumnRow: React.FC<ColumnRowProps> = ({ title, columnData }) => {
  const isEmpty = columnData.length === 0;
  return (
    <>
      {!isEmpty ? (
        <div className="pt-4 px-4 overflow-hidden">
          <div className="flex items-center mb-3 text-base font-bold text-bluegray-700">
            {title}
          </div>
          <ul className="asset-info-banner-list">
            {columnData.map((p, i) => {
              return <Column {...p} key={i} />;
            })}
          </ul>
        </div>
      ) : null}
    </>
  );
};

const Title: React.FC<Record<string, unknown>> = () => (
  <div className="h-22.5 flex items-end p-4 text-lg font-bold text-bluegray-700 leading-tight">
    <img
      src="/static/images/report/icon-balloon.svg"
      alt=""
      className="h-4 w-4 mr-2"
    />
    あなたにおすすめ！お役立ちコラム
  </div>
);

export const ColumnTable = ({
  Articles,
  userName,
  from,
}: {
  Articles: api.ReportArticle[];
  userName: string;
  from: number;
}) => {
  const month = (yyyymmddToDate(from.toString()) || new Date()).getMonth() + 1;
  const types = Object.values(ARTICLE_TYPE);
  const columnRowData = types.map(type => {
    const title =
      type === ARTICLE_TYPE.MONTHLY_COLUMN
        ? getArticleCategoryTitle(type, month)
        : getArticleCategoryTitle(type);
    return {
      title: title,
      columnData: Articles.filter(a => a.Type === type).map(a => {
        return {
          title: a.Title,
          imgSrc: a.ImageUrl,
          link: a.ContentUrl,
        };
      }),
    };
  });

  const isEmpty = columnRowData.length === 0;
  return (
    <>
      {!isEmpty ? (
        <>
          <Title />
          <div className="bg-white">
            {columnRowData.map(p => (
              <ColumnRow {...p} key={p.title} />
            ))}
            <Hint userName={userName} />
          </div>
        </>
      ) : null}
    </>
  );
};
